import { Link, useSearchParams } from "react-router-dom";
import { useVisitReport, type VisitReport } from "../../api/routing";
import { Stat } from "../../components/ui";
import "../../styles/routing.css";
import { ReportControls } from "./ReportControls";
import { Breakdowns, VisitsTable } from "./ReportTables";
import ReportsTabs from "../reports/ReportsTabs";
import { queryFromSearch, reportApiUrl, searchFromQuery, type ReportQuery } from "./reportQuery";

/** The visits report: every visit planned for a day, a week, a month or a
 * range, and what became of it -- by crew, by day and one by one -- narrowed
 * by crew, outcome, plan, region or a search, and downloaded as a workbook or
 * a PDF to send to the people who asked. The address carries the whole view,
 * so the link can be sent as well. */
export default function ReportPage() {
  const [params, setParams] = useSearchParams();
  const query = queryFromSearch(params);
  const report = useVisitReport(reportApiUrl(query));
  const data = report.data;

  function update(change: Partial<ReportQuery>) {
    setParams(searchFromQuery({ ...query, ...change }), { replace: true });
  }

  return (
    <div className="page-bleed routing-page">
      <ReportsTabs />
      <div className="routing-content">
        <header className="schedule-head">
          <h1>Site visits report</h1>
          {data ? <span className="report-period">{data.period.label}</span> : null}
          {data ? (
            <div className="schedule-nav">
              <a className="btn btn-primary" href={data.urls.pdf}>
                Download PDF
              </a>
              <a className="btn" href={data.urls.xlsx}>
                Download Excel
              </a>
            </div>
          ) : null}
        </header>
        <ReportControls query={query} report={data} onChange={update} />
        {report.isPending ? <p className="app-loading">Loading the report…</p> : null}
        {report.isError ? <p className="notice bad">The report could not be loaded: {report.error.message}</p> : null}
        {data ? <Body report={data} query={query} onChange={update} /> : null}
      </div>
    </div>
  );
}

function Body({ report, query, onChange }: { report: VisitReport; query: ReportQuery; onChange: (change: Partial<ReportQuery>) => void }) {
  const total = report.total;
  const toggle = (outcome: string) => () => onChange({ outcome: query.outcome === outcome ? "" : outcome });
  return (
    <>
      <div className="stat-strip" aria-label="Totals">
        <Stat label="Visits planned" value={total.planned} pressed={query.outcome === ""} onClick={() => onChange({ outcome: "" })} />
        <Stat
          label={`Done${total.completion === null ? "" : ` · ${total.completion}%`}`}
          value={total.done}
          good
          tone="var(--ok-ink)"
          pressed={query.outcome === "done"}
          onClick={toggle("done")}
        />
        <Stat label="Not done" value={total.not_done} tone="var(--warning)" pressed={query.outcome === "not_done"} onClick={toggle("not_done")} />
        <Stat label="Pending" value={total.pending} tone="var(--ink-soft)" pressed={query.outcome === "pending"} onClick={toggle("pending")} />
        <Stat label="Pending, past their day" value={total.overdue} tone="var(--blocking)" pressed={query.outcome === "overdue"} onClick={toggle("overdue")} />
        <Stat label="With a visit sheet" value={total.sheets} />
      </div>
      <p className="report-filter-text muted">
        {report.filters.text}. Visits on archived plans are left out, as on the Schedule.
      </p>
      {report.rows.length === 0 ? (
        <p className="empty-state card">
          No visits were planned for {report.period.label.toLowerCase().startsWith("week") ? "the " : ""}
          {report.period.label} under this filter. Visits are planned on the <Link to="/routing">Planner</Link> and assigned on{" "}
          <Link to="/routing/plans">Plans</Link>.
        </p>
      ) : (
        <>
          <Breakdowns report={report} onCrew={(id) => onChange({ provider_id: String(id) })} />
          <h2 className="report-subhead">
            {report.rows.length} visit{report.rows.length === 1 ? "" : "s"}
          </h2>
          <VisitsTable rows={report.rows} />
        </>
      )}
    </>
  );
}
